/* global XMLHttpRequest */

import XlsxPopulate from 'xlsx-populate'

/* @ngInject */
const excelUtils = (
  utils,
  valueHandler,
  paths,
  $filter,
  $$wagon,
  matcher,
  types,
  excelStyles,
  toaster,
) => {
  const self = {}

  const letters = utils.getLettersRange('A', 'Z')

  const verifyIndex = (index, functionName = 'function', indexName = 'index') => {
    if (valueHandler.isNull(index)) {
      throw new Error('Error in ' + functionName + ' : ' + indexName + ' is null')
    }
    if (!valueHandler.isNumber(index) || index < 0) {
      throw new Error('Error in ' + functionName + ' : ' + indexName + ' is invalid')
    }
  }

  self.integerToLetter = (index) => {
    verifyIndex(index, 'integerToLetter')
    let firstLetter = ''
    const secondLetter = letters[index % 26]
    if (index > 25) {
      firstLetter = letters[parseInt(index / 26) - 1]
    }
    return firstLetter + secondLetter
  }

  self.getColumnName = (colIndex) => {
    verifyIndex(colIndex, 'getColumnName', 'colIndex')
    return self.integerToLetter(colIndex)
  }

  self.getColumn = (sheet, colIndex) => {
    return sheet.column(self.getColumnName(colIndex))
  }

  self.getCellName = (rowIndex, colIndex) => {
    verifyIndex(rowIndex, 'getCellName', 'rowIndex')
    verifyIndex(colIndex, 'getCellName', 'colIndex')
    return self.integerToLetter(colIndex) + rowIndex
  }

  self.getCell = (sheet, rowIndex, colIndex) => {
    if (valueHandler.isNumber(colIndex)) {
      return sheet.cell(self.getCellName(rowIndex, colIndex))
    } else if (valueHandler.isString(colIndex)) {
      return sheet.cell(colIndex + rowIndex)
    }
  }

  self.getRangeName = (rowIndex, colIndex, rowLength = 0, colLength = 0) => {
    verifyIndex(rowIndex, 'getRange', 'rowIndex')
    verifyIndex(colIndex, 'getRange', 'colIndex')
    const firstCell = self.getCellName(rowIndex, colIndex)
    const lastCell = self.getCellName(rowIndex + rowLength, colIndex + colLength)
    return firstCell + ':' + lastCell
  }

  self.getRange = (sheet, rowIndex, colIndex, rowLength = 0, colLength = 0) => {
    return sheet.range(self.getRangeName(rowIndex, colIndex, rowLength, colLength))
  }

  self.getLongestTextSize = (range, isDate = false) => {
    const longestMessage = range.reduce((max, cell) => {
      let value = cell.value()
      if (isDate) {
        if (max === 0) {
          if (valueHandler.isNull(value)) return max
          value = value.toString()
          return Math.max(max, value.length)
        }
        return max
      } else {
        if (valueHandler.isNull(value)) return max
        value = value.toString()
        return Math.max(max, value.length)
      }
    }, 0)
    return parseInt((longestMessage + 5) * 0.87)
  }

  self.setCellValue = (cell, value, type = types.text()) => {
    value = $filter('excel')(value, type.excelNature)

    if (matcher.contain(value, '=')) {
      cell.formula(value)
    } else {
      cell.value(value)
    }

    if (type.excelFormat) {
      cell.style('numberFormat', type.excelFormat)
    }
  }

  self.setCell = (sheet, cellInfo, _style = excelStyles.noStyle) => {
    const cell = sheet.cell(cellInfo.name)
    self.setCellValue(cell, cellInfo.value, cellInfo.type)
    const style = cellInfo.style || _style
    if (cellInfo.mergeRange) {
      const range = sheet.range(cellInfo.mergeRange)
      range.merged(true)
      range.style(style)
    } else {
      cell.style(style)
    }
  }

  /**
   * return an approximation of the width needed by a value
   */
  self.getFieldWidth = (value) => {
    return (value.toString().length / (1 + value.toString().length * 0.01)) - 1.5
  }

  self.setCellHeight = (sheet, cellName, cellValue) => {
    if (!cellValue || !cellName || matcher.contain(cellValue, '=')) return
    const name = cellName.toString()
    const col = sheet.column(name.replace(/[0-9]/g, ''))
    const row = sheet.row(+name.replace(/[a-z]/gi, ''))

    const fieldWidth = self.getFieldWidth(cellValue)
    let numberLine = 0
    if (col.width() < fieldWidth)
      numberLine = Math.floor(fieldWidth / col.width())
    const height = 18 + 14 * numberLine

    if (row.height() <= height) row.height(height)
  }

  self.setColorStyles = (cell, data, fieldName) => {
    if (fieldName === 'completeRevisionExcel') {
      const color = $$wagon.test.getRevisionColor(data['revisionDate'], data['step'], data['extendRevision'])
      if (color === 'badge-revision-1')
        cell.style('fill', 'FFFF00')
      else if (color === 'badge-revision-2')
        cell.style('fill', 'ffa500')
      else if (color === 'badge-revision-3')
        cell.style('fill', 'FF0000')
      else if (color === 'badge-revision-4')
        cell.style('fill', '800080')
    } else if (fieldName === 'arrivalDate') {
      if (!$$wagon.test.isDateTrainOk(data['arrivalDate'])) {
        cell.style('fill', 'FAEE45')
      }
    } else if (fieldName === 'isolated') {
      if (data['isolated']) {
        cell.style('fill', 'E69821')
      }
    } else if (fieldName === 'damaged') {
      if (data['damaged']) {
        cell.style('fill', 'E00065')
      }
    } else if (fieldName === 'availableLength' || fieldName === 'ratioAvailableLength') {
      if (data['availableLength'] < 0 || data['ratioAvailableLength'] < 0) {
        cell.style('fill', 'fa1212')
      }
    } else if (fieldName === 'commentaire') {
      if (data[fieldName]) {
        cell.style('fill', '4c4cff')
      }
    } else if (fieldName === 'id') {
      if (data[fieldName] && data['idNew']) {
        cell.style('fontColor', 'ff0000')
      }
    }
  }

  self.setColorId = (cell, data, fieldName) => {
    if (fieldName === 'id') {
      if (data[fieldName] && data['idNew']) {
        cell.style('fontColor', 'ff0000')
      }
    }
  }

  self.howManyValidFields = (fields) => {
    let colIndex = 0
    for (const key in fields) {
      if (fields[key].output && fields[key].output.excel && fields[key].output.excel.display) {
        colIndex++
      }
    }
    return colIndex
  }

  self.setFitColWidth = (sheet, colIndex, rowIndex, rowLength, fieldName = null) => {
    const column = self.getColumn(sheet, colIndex)
    const range = self.getRange(sheet, rowIndex, colIndex, rowLength, 0)
    const isDate = matcher.containDate(fieldName)
    let width = self.getLongestTextSize(range, isDate)
    if (isDate && width < 15) {
      width = 15
    }
    column.width(width)
  }

  self.getNumberLine = (sheet, wagon, order) => {
    let colIndex = 1
    let colWidth = 0
    let fieldWidth = 0
    let numberLine = 1

    order.forEach(fieldName => {
      if (wagon[fieldName]) {
        colWidth = sheet.column(colIndex).width()
        fieldWidth = (wagon[fieldName].toString().length * 0.7) + 1

        if (colWidth < fieldWidth) {
          numberLine = Math.ceil(fieldWidth / colWidth)
        }
      }
      colIndex++
    })

    return numberLine
  }

  self.getOwnersOf = (wagons) => {
    const owners = []
    let add = true

    for (let wagonIndex = 0; wagonIndex < wagons.length; wagonIndex++) {
      const wagon = wagons[wagonIndex]
      add = true
      for (let ownerIndex = 0; ownerIndex < owners.length; ownerIndex++) {
        const owner = owners[ownerIndex]
        if (owner === wagon.owner) {
          add = false
          break
        }
      }
      if (add) {
        owners.push(wagon.owner)
      }
    }

    return owners
  }

  const Promise = XlsxPopulate.Promise

  self.getWorkbook = (filename) => {
    if (filename === 'park' || filename === 'unpark') {
      filename += 'Certificate'
    }
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      const url = paths.excel_files + filename + '.xlsx'
      req.open('GET', url, true)
      req.responseType = 'arraybuffer'
      req.onreadystatechange = () => {
        if (req.readyState === 4) {
          if (req.status === 200) {
            resolve(XlsxPopulate.fromDataAsync(req.response))
          } else {
            const error = new Error('Received a ' + req.status + ' HTTP code.')
            reject(error)
          }
        }
      }
      req.send()
    })
  }

  self.downloadWorkbook = (blob, filename = 'file') => {
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename + '.xlsx')
    } else {
      var url = window.URL.createObjectURL(blob)
      var a = document.createElement('a')
      document.body.appendChild(a)
      a.href = url
      a.download = filename + '.xlsx'
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toaster.message('Téléchargement terminé')
    }
  }

  return self
}
export default excelUtils



// WEBPACK FOOTER //
// ./public/javascript/sygem/global/services/excel/excel.utils.js